/**
 * Persists business-office / report updates for an order.
 * @module updateReports
 */

import {
  UpdateReportsRequest,
  UpdateReportsResponse,
} from "../../data-model/updateReportsRequest";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { throwApiError, throwIfSiblingFailures } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/**
 * POSTs report updates. Failures throw an `Error` carrying `status` + `detail`
 * per the `/prior-auth-utilities/*` HTTP-status contract; pair with
 * `showApiError` on the consumer side.
 *
 * @param body - {@link UpdateReportsRequest} payload.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns Parsed JSON response on success (no sibling failures).
 * @throws {Error} On HTTP failure, sibling propagation failure, network failure, or parse failure.
 */
export const updateReports = async (
  body: UpdateReportsRequest,
  signal?: AbortSignal,
): Promise<UpdateReportsResponse> => {
  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.UPDATE_REPORTS, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      ...(signal && { signal }),
    });

    if (!response.ok) {
      await throwApiError(response);
    }

    const result = (await response.json()) as UpdateReportsResponse;
    throwIfSiblingFailures(result);
    return result;
  } catch (error) {
    console.error("Failed to update reports:", error);
    throw error;
  }
};
